import type React from "react";
import { useCallback, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { UploadIcon } from "lucide-react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { userIsConnectedTo } from "simplydo/core/connections";
import { useCurrentUser } from "simplydo/react/hooks/useCurrentUser";
import { MicrosoftIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { DropdownItem } from "simplydo/react/ui/dropdown";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";
import { Progress } from "simplydo/react/ui/progress";

import api from "api";

import { DropZone, type DropZoneConfigProps } from "./DropZone";
import {
  FileUploadWrapper,
  type FileUploadWrapperProps,
  type FileUploadWrapperRef,
} from "./FileUploadWrapper";
import {
  DEFAULT_MAX_MB_FILE_SIZE,
  type DragFile,
  type UploadState,
} from "./uploadUtils";

export type FileUploaderProps = {
  popupProps?: any;
  dropzoneProps?: DropZoneConfigProps;
  style?: React.CSSProperties;
} & Omit<
  FileUploadWrapperProps,
  "children" | "setUploadState" | "setDraggedOverFiles"
>;

export const FileUploaderPopup = ({
  accept,
  forType,
  forId,
  onError,
  popupProps = {},
  dropzoneProps = {},
  onComplete,
  allowMultiple,
  onCompleteMultiple,
  maxMBFileSize,
}: FileUploaderProps) => {
  const { t } = useTranslation();
  const uploadRef = useRef<FileUploadWrapperRef>(null);

  const [draggedOverFiles, setDraggedOverFiles] = useState<Array<DragFile>>([]);
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    uploadProgress: 0,
  });

  const user = useCurrentUser(api);
  const connected365 = userIsConnectedTo(user, "office365");

  const [open, setOpen] = useState(false);
  const openPopup = useCallback(() => setOpen(true), []);
  const chooseFile = useCallback(() => {
    uploadRef.current?.chooseFile();
    setOpen(false);
  }, []);
  const chooseOneDriveFile = useCallback(() => {
    uploadRef.current?.openOneDrive();
    setOpen(false);
  }, []);

  return (
    <FileUploadWrapper
      accept={accept}
      forType={forType}
      forId={forId}
      ref={uploadRef}
      setUploadState={setUploadState}
      setDraggedOverFiles={setDraggedOverFiles}
      maxMBFileSize={maxMBFileSize}
      onComplete={onComplete}
      allowMultiple={allowMultiple}
      onCompleteMultiple={onCompleteMultiple}
      onError={onError}
    >
      {connected365 ? (
        <Popup
          style={{ zIndex: 1500 }}
          on="click"
          open={open}
          position="top center"
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          trigger={
            <div
              tabIndex={0}
              role="button"
              onKeyDown={handleClickForKeyboard(openPopup)}
            >
              <DropZone
                uploadState={uploadState}
                setUploadState={setUploadState}
                draggedOverFiles={draggedOverFiles}
                disabled={!!popupProps.disabled}
                allowMultiple={allowMultiple}
                accept={accept}
              />
            </div>
          }
          {...popupProps}
        >
          {
            <div>
              <H5>
                Upload a file
                <Subheader size="small">
                  The maximum file size is{" "}
                  {maxMBFileSize ?? DEFAULT_MAX_MB_FILE_SIZE}MB.
                </Subheader>
              </H5>

              <div>
                {uploadState.isUploading && (
                  <Progress
                    percent={uploadState.uploadProgress}
                    indicating
                    progress
                    style={{ margin: 5 }}
                  />
                )}
                <DropdownItem onClick={chooseFile}>
                  <Icon icon={UploadIcon} />
                  {t("ideas.actions.chooseFile")}
                </DropdownItem>
                {userIsConnectedTo(user, "office365") ? (
                  <DropdownItem onClick={chooseOneDriveFile}>
                    <Icon icon={MicrosoftIcon} /> Choose from OneDrive
                  </DropdownItem>
                ) : null}
              </div>

              <div style={{ textAlign: "left", marginTop: 20 }}>
                <Button
                  size="small"
                  onClick={() => {
                    setOpen(false);
                  }}
                  variant="outline"
                >
                  {t("generic.cancel")}
                </Button>
              </div>
            </div>
          }
        </Popup>
      ) : (
        <DropZone
          uploadState={uploadState}
          setUploadState={setUploadState}
          draggedOverFiles={draggedOverFiles}
          disabled={!!popupProps.disabled}
          allowMultiple={allowMultiple}
          accept={accept}
          onClick={() => {
            uploadRef.current?.chooseFile();
          }}
        />
      )}
    </FileUploadWrapper>
  );
};
